import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Tilt } from 'react-tilt';

const AboutSection = styled.section`
  padding: 100px 150px;
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const AboutContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const AboutTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const AboutContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 50px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AboutContent = styled(motion.div)`
  color: var(--text-secondary);
  font-size: 18px;
  
  p {
    margin-bottom: 20px;
    line-height: 1.7;
  }
  
  a {
    color: var(--accent-primary);
    display: inline-block;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      transform: scaleX(0);
      height: 1px;
      bottom: 0;
      left: 0;
      background-color: var(--accent-primary);
      transform-origin: bottom right;
      transition: transform 0.25s ease-out;
    }
    
    &:hover::after {
      transform: scaleX(1);
      transform-origin: bottom left;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const Portrait = styled(motion.div)`
  position: relative;
  max-width: 300px;
  
  @media (max-width: 768px) {
    margin: 50px auto 0;
  }
`;

const StyledImage = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background-color: transparent;
  
  svg {
    width: 100%;
    height: 100%;
    z-index: 1;
    overflow: visible;
  }
`;

const BorderLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.2,
      duration: 0.5
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const About = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [contentRef, contentInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [photoRef, photoInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const tiltOptions = {
    max: 25,
    scale: 1.05,
    speed: 300
  };
  
  return (
    <AboutSection id="about">
      <AboutContainer>
        <AboutTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          About Me
        </AboutTitle>
        
        <AboutContentWrapper>
          <AboutContent
            ref={contentRef}
            initial={{ y: 20, opacity: 0 }}
            animate={contentInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p>
              Hello! I'm Nandan, a cybersecurity consultant specializing in vulnerability assessment and penetration testing. My journey in cybersecurity began during my academics, where I discovered my passion for identifying and addressing security weaknesses in software systems.
            </p>
            <p>
              With over 4 years of professional experience, I've helped organizations identify critical vulnerabilities, develop secure coding practices, and implement robust security measures. My expertise spans across mobile application security, web application security, API security, AI security, cloud security, thick client and network & infrastructure security.
            </p>
            <p>
              I currently work at <a href="https://home.kpmg/in/en/home.html" target="_blank" rel="noopener noreferrer">KPMG India</a> as a Consultant, where I perform penetration testing for major financial institutions, develop security automation tools, and provide expert guidance on security engineering challenges.
            </p>
            <p>
              Beyond my professional work, I'm an active bug bounty hunter, having successfully identified vulnerabilities for organizations like OpenAI, Cloudflare, and WhatsApp. I'm also passionate about creating security tools that help others in the cybersecurity community.
            </p>
          </AboutContent>
          
          <Portrait
            ref={photoRef}
            initial={{ x: 50, opacity: 0 }}
            animate={photoInView ? { x: 0, opacity: 1 } : { x: 50, opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Tilt options={tiltOptions}>
              <StyledImage>
                <svg viewBox="-50 -50 300 320" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Shield Glow Filter */}
                  <defs>
                    <filter id="glow" filterUnits="userSpaceOnUse" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
                      <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.4 0 0 0 0 1 0 0 0 0 0.854 0 0 0 1 0" result="glow" />
                      <feBlend in="SourceGraphic" in2="glow" mode="normal" />
                    </filter>

                    <filter id="yellowGlow" filterUnits="userSpaceOnUse" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                      <feColorMatrix in="blur" type="matrix" values="0 0 0 0 0.97 0 0 0 0 0.87 0 0 0 0 0.12 0 0 0 1 0" result="yellowGlow" />
                      <feBlend in="SourceGraphic" in2="yellowGlow" mode="normal" />
                    </filter>
                  </defs>
                  
                  {/* Shield Outer */}
                  <path 
                    d="M100 10 L20 50 L20 120 C20 160 50 190 100 210 C150 190 180 160 180 120 L180 50 L100 10Z" 
                    fill="rgba(10, 25, 47, 0.7)"
                    stroke="#64ffda" 
                    strokeWidth="4"
                    style={{ filter: 'url(#glow)' }}
                  />
                  
                  {/* Inner Shield */}
                  <path 
                    d="M100 30 L40 60 L40 115 C40 145 60 170 100 185 C140 170 160 145 160 115 L160 60 L100 30Z" 
                    fill="transparent"
                    stroke="#64ffda" 
                    strokeWidth="1.5"
                    strokeOpacity="0.8"
                  />
                  
                  {/* Cross/Plus */}
                  <rect 
                    x="95" 
                    y="70" 
                    width="10" 
                    height="60" 
                    rx="5" 
                    fill="#f7df1e" 
                    style={{ filter: 'url(#yellowGlow)' }}
                  />
                  <rect 
                    x="70" 
                    y="95" 
                    width="60" 
                    height="10" 
                    rx="5" 
                    fill="#f7df1e" 
                    style={{ filter: 'url(#yellowGlow)' }}
                  />
                </svg>
              </StyledImage>
            </Tilt>
            <BorderLayer />
          </Portrait>
        </AboutContentWrapper>
      </AboutContainer>
    </AboutSection>
  );
};

export default About; 